/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import ReactGA, { set } from 'react-ga';
import useTranslation from 'hooks/useTranslation';
import dynamic from 'next/dynamic';

import { Form } from '@unform/web';
import loadable from '@loadable/component';
import { Typography, CircularProgress } from '@material-ui/core';
import useStyles from 'styles/sorteio-numeros';

import { withStyles } from '@material-ui/core/styles';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';

const Box = loadable(() => import('../components/Box'));
const ContainerBox = loadable(() => import('../components/ContainerBox'));
const PageTitle = loadable(() => import('../components/PageTitle'));
const Header = loadable(() => import('../components/Header'));
const Footer = loadable(() => import('../components/Footer'));
const Select = loadable(() => import('../components/Select'));
const Input = loadable(() => import('../components/Input'));
const Button = loadable(() => import('../components/Button'));
const Switch = loadable(() => import('../components/Switch'));

const HeadSeo = dynamic(() => import('../components/HeadSeo'), {
  loading: () => <div>Loading...</div>,
});

interface NumberFormInputs {
  numberA: number;
  numberB: number;
  numberC: number;
}

interface newDrawnProps {
  id: number;
  time: Date;
  numbers: number[];
}

const Accordion = withStyles({
  root: {
    width: '100%',
    borderRadius: '5px',
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    borderRadius: '5px',
    backgroundColor: 'rgba(0, 0, 0, .03)',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiAccordionDetails);

const faq = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  name: 'Perguntas Frequentes',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Sorteio de números é grátis?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sim! O nosso Sorteio de números é totalmente gratuito!',
      },
    },
    {
      '@type': 'Question',
      name: 'Como funciona o Sorteio de Números?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O Sorteio de Números é uma ferramenta online para sortear números de forma 100% aleatória. É só definir uma escala mínima e máxima e a quantidade de números que deseja sortear. Pronto, agora é só contar com a sorte!',
      },
    },
    {
      '@type': 'Question',
      name: 'Como fazer um sorteio de números?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Para fazer o seu sorteio de números basta acessar instasorteio.com, ir até "NOSSOS SORTEIOS" e selecionar nos produtos "Sorteio de Números".
      
1º - Escola a quantidade de números que deseja sortear.
2º - Defina o intervalo mínimo e máximo.
3º - Personalize o seu sorteio com nossas animações.

Pronto! Agora, é só realizar o sorteio.
    `,
      },
    },
    {
      '@type': 'Question',
      name: 'O Sorteio de Números é aleatório?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: `Sim! 

O Sorteio de Números é uma ferramenta 100% aleatória! 
    
Além de ser gratuita, permite que você crie uma série de números aleatórios começando de um mínimo e um máximo de números possíveis.`,
      },
    },
  ],
};

const SorteioDeNumeros: React.FC = () => {
  ReactGA.pageview('/sorteio-de-numeros');
  const { t } = useTranslation();
  const classes = useStyles();
  const [expanded, setExpanded] = useState<string | false>('panel1');
  const [checkAnimateCountDown] = useState(false);
  const [numeros, setNumeros] = useState<any>([]);
  const [checkNoRepeatNumber, setCheckNoRepeatNumber] = useState(true);
  const [possibilits, setPossibilits] = useState(false);
  const [checkShowResultsLink] = useState(false);
  const [checkOrderNumber] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [drawn, setDrawn] = useState([] as newDrawnProps[]);
  const [id, setId] = useState(1);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const onSubmit = async (data: NumberFormInputs) => {
    // return;
    setLoading(true);
    showModal && setShowModal(false);

    // scroll to top
    scrollToTop();

    // All numbers drawn
    const results: number[] = [];
    drawn.map(item => item.numbers.map(number => results.push(number)));

    // Request to API
    const response = await fetch(`/sorteio-de-numeros/api/generate-numbers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data,
        results,
        checkNoRepeatNumber,
        checkOrderNumber,
      }),
    });

    const res = await response.json();

    if (res.data[0] > 0) {
      setId(id + 1);

      const newNumber = [
        {
          id: id,
          time: new Date(),
          numbers: res.data,
        },
      ] as newDrawnProps[];
      setDrawn(newNumber.concat(drawn));
    } else {
      setShowModal(true);
      setLoading(false);
    }

    if (res.data[0] === 0) {
      setPossibilits(true);
      setLoading(false);
      return;
    } else {
      setNumeros([...numeros, res.data]);
    }

    setPossibilits(false);

    // Set form values to localStorage
    localStorage.setItem(
      '@instasorteio:drawnFormValues',
      JSON.stringify({
        data,
        checkAnimateCountDown,
        checkNoRepeatNumber,
        checkShowResultsLink,
        checkOrderNumber,
      }),
    );
    setLoading(false);
  };

  const handleChange =
    (panel: string) => (event: React.ChangeEvent<{}>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  return (
    <>
      <script
        type="application/ld+json"
        id="faq"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faq),
        }}
      />
      <HeadSeo />
      <div style={{ height: 70 }}>
        <Header />
      </div>
      <ContainerBox>
        <PageTitle title={t('drawNumbers')} />
        <Box>
          <Typography variant="body2">{t('subtitleNumbers')}</Typography>

          <Form
            onSubmit={onSubmit}
            className={classes.form}
            initialData={{
              numberB: 1,
              numberC: 10,
            }}
          >
            <div className={classes.formContent}>
              <div className={classes.options}>
                <div style={{ width: '500%' }}>{t('numbersToBeDrawn')}</div>
                <Select name="numberA" className={classes.input}>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="7">7</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                </Select>
              </div>

              <div className={classes.numberRange}>
                <label htmlFor="numberB">{t('pickNumbersBetween')}</label>
                <Input type="number" name="numberB" className={classes.input} />
                <label htmlFor="maxNumber">{t('maxNumber')}</label>
                <Input type="number" name="numberC" className={classes.input} />
              </div>

              <div className={classes.options}>
                <div>{t('allowDuplicates')}</div>
                <Switch
                  checked={checkNoRepeatNumber}
                  onChange={() => {
                    setCheckNoRepeatNumber(!checkNoRepeatNumber);
                  }}
                />
              </div>
              {/* {allowDuplicates && <div>Possibilidades: {possibilit}</div>} */}

              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={20} />
                </div>
              ) : (
                <Button disabled={possibilits} type="submit">
                  Gerar Números
                </Button>
              )}

              {possibilits && (
                <div style={{ textAlign: 'center' }}>
                  Acabaram as possibilidades, atualize a página e tente
                  novamente.
                </div>
              )}

              {drawn.length > 0 && (
                <div>
                  {drawn.map((n: any, index: number) => {
                    return (
                      <div
                        key={n.id}
                        className={classes.result}
                        style={{ display: 'flex', flexDirection: 'column' }}
                      >
                        <p style={{ fontSize: 12, fontWeight: 'bold' }}>
                          {drawn.length - index}º Sorteado
                        </p>
                        <p
                          style={{
                            fontSize: 18,
                            fontWeight: 'bold',
                            marginTop: -7,
                          }}
                        >
                          {n.numbers
                            .sort((a: number, b: number) => a - b)
                            .join(', ')}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Form>

          <Typography
            style={{
              fontWeight: 'bold',
              fontSize: 24,
              marginBottom: 20,
            }}
            variant="h3"
          >
            Perguntas frequentes
          </Typography>

          <Accordion
            square
            expanded={expanded === 'panel1'}
            onChange={handleChange('panel1')}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
            >
              <Typography>1. Sorteio de número é grátis?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Sim! O nosso Sorteio de números é totalmente gratuito!
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            square
            expanded={expanded === 'panel2'}
            onChange={handleChange('panel2')}
          >
            <AccordionSummary
              aria-controls="panel2d-content"
              id="panel2d-header"
            >
              <Typography>2. Como funciona o Sorteio de Números?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                O Sorteio de Números é uma ferramenta online para sortear
                números de forma 100% aleatória. É só definir uma escala mínima
                e máxima e a quantidade de números que deseja sortear. Pronto,
                agora é só contar com a sorte!
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            square
            expanded={expanded === 'panel3'}
            onChange={handleChange('panel3')}
          >
            <AccordionSummary
              aria-controls="panel3d-content"
              id="panel3d-header"
            >
              <Typography>3. Como fazer um sorteio de números?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Para fazer o seu sorteio de números basta acessar
                instasorteio.com, ir até "NOSSOS SORTEIOS" e selecionar nos
                produtos "Sorteio de Números".
                <br />
                <br />
                1º - Escola a quantidade de números que deseja sortear.
                <br />
                2º - Defina o intervalo mínimo e máximo.
                <br />
                3º - Personalize o seu sorteio com nossas animações.
                <br />
                <br />
                Pronto! Agora, é só realizar o sorteio.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            square
            expanded={expanded === 'panel4'}
            onChange={handleChange('panel4')}
          >
            <AccordionSummary
              aria-controls="panel4d-content"
              id="panel4d-header"
            >
              <Typography>4. O Sorteio de Números é aleatório?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Sim!
                <br />
                <br />
                O Sorteio de Números é uma ferramenta 100% aleatória!
                <br />
                <br />
                Além de ser gratuita, permite que você crie uma série de números
                aleatórios começando de um mínimo e um máximo de números
                possíveis.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </ContainerBox>
      <Footer />
    </>
  );
};

export default SorteioDeNumeros;
